import { Component, OnInit, Inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthSdkError, OktaAuth } from '@okta/okta-auth-js';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ILogger } from '@shure/shared/angular/utils/logging';

/**
 * This component is a replacement for the OktaSigninCallback component. The reason is when that component detected
 * any kind of error during signin/callback, it would simply put up a black error page, and sometimes display a cryptic
 * error message. Not a good UX.
 *
 * This component will catch the errors thrown from calling `handleLoginRedirect` and reroute the application to a
 * provided fallback path.  This component also guards against a user trying to navigate directly to this component,
 * which also resulted in the black okta error page, and will reroute the application to the fallback path.
 *
 * The reason(s) for the errors thrown from calling `handleLoginRedirect` are usually a result of two or more tabs
 * trying to login to Okta at the same time. Since the tokens are shared across tabs, there are some contention issues
 * that are exposed. Okta recommends over-riding that component and providing behavior suitable for your application.
 * The changes in this file attempt to mediate and recover the situation by re-routing to app away from
 * this route and to a route appropriate for the apps current auth state.
 */

@Component({
	template: ''
})
export class CustomOktaSigninCallbackComponent implements OnInit {
	public error?: string;
	private fallbackPath = '/';
	private queryParams: Params;

	constructor(
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		private activatedRoute: ActivatedRoute,
		private oktaInterfaceService: OktaInterfaceService,
		private readonly router: Router,
		private readonly logger: ILogger
	) {
		this.logger = logger.createScopedLogger('CustomOktaSigninCallback');
		// eslint-disable-next-line dot-notation
		this.fallbackPath = activatedRoute.snapshot.data['fallbackPath'] ?? '/';
		this.queryParams = this.activatedRoute.snapshot.queryParams;
		this.logger.debug('constructor', 'activatedRoute.snapshot %o', { snapshot: activatedRoute.snapshot });
	}

	private isAuthenticated = toSignal(this.oktaInterfaceService.$isUserAuthenticated);

	public async ngOnInit(): Promise<void> {
		// detect a user trying to navigate directly to this component by checking
		// for the presence of the 'code' and 'state' query parameters. If they
		// are not present, navigate to the fallbackPath

		if (!('code' in this.queryParams && 'state' in this.queryParams)) {
			this.logger.error('ngOnInit', 'missing required queryParams. routing to: ', this.fallbackPath);
			this.router.navigate([this.fallbackPath]);
			return;
		}

		try {
			await this.oktaAuth.handleLoginRedirect();
		} catch (error) {
			this.logger.error('ngOnInit.handleLoginRedirect() exception', 'Error: %o', {
				error: (<Error>error).toString(),
				userAuthenticated: this.isAuthenticated(),
				fallbackPath: this.fallbackPath
			});

			if (error instanceof AuthSdkError) {
				this.oktaAuth.transactionManager.clear();
			}

			if (!this.isAuthenticated()) {
				this.oktaAuth.tokenManager.clear();
			}

			// In all cases, navigate to the designated fallbackPath.
			// That route should properly handle re-routing to Okta if the user isn't
			// logged in, or displaying the apps default route.
			this.router.navigate([this.fallbackPath]);
		}
	}
}
